import React, { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ImportFormatsHelp } from "./ImportFormatsHelp";
import { ResolveModal } from "./ResolveModal";

export function ImportEntriesButton({ competitionId, onImported }) {
  const fileRef = useRef(null);
  const [pickedFile, setPickedFile] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  // stage: "idle" | "previewing" | "resolving" | "committing" | "done"
  const [stage, setStage] = useState("idle");
  const [preview, setPreview] = useState(null); // { matched, unmatched, total_rows_referenced }
  const [choices, setChoices] = useState({});   // typed_name → photographer_id (or "" to leave unmatched)
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const reset = () => {
    setPickedFile(null);
    setPreview(null);
    setChoices({});
    setResult(null);
    setError("");
    setStage("idle");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPickedFile(f);
    setError("");
    setStage("previewing");
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("competition_id", competitionId);
      const { data } = await api.post(`/import/entries/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      // Seed choices with the top suggestion for each unmatched name so the
      // operator only has to override when the auto-pick is wrong.
      const seeded = {};
      for (const u of data.unmatched || []) {
        seeded[u.typed_name] = u.suggestions?.[0]?.id || "";
      }
      setChoices(seeded);
      setStage("resolving");
    } catch (ex) {
      setError(formatApiError(ex.response?.data?.detail) || ex.message);
      setStage("idle");
    }
  };

  const commit = async () => {
    if (!pickedFile) return;
    setStage("committing");
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", pickedFile);
      fd.append("competition_id", competitionId);
      fd.append("update_existing", updateExisting ? "true" : "false");
      const overrides = Object.fromEntries(
        Object.entries(choices).filter(([, v]) => v),
      );
      fd.append("name_overrides", JSON.stringify(overrides));
      const { data } = await api.post(`/import/entries`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setStage("done");
      await onImported();
    } catch (ex) {
      setError(formatApiError(ex.response?.data?.detail) || ex.message);
      setStage("resolving");
    }
  };

  const openPicker = () => {
    reset();
    setTimeout(() => fileRef.current?.click(), 0);
  };

  const busy = stage === "previewing" || stage === "committing";

  return (
    <>
      <div className="relative flex items-center gap-3">
        <label
          className="flex items-center gap-1.5 muted-text text-xs cursor-pointer select-none"
          title="When ON, rows matching an existing photographer + image title will have their score & placement updated instead of being treated as duplicates."
        >
          <input
            type="checkbox"
            checked={updateExisting}
            onChange={(e) => setUpdateExisting(e.target.checked)}
            className="accent-amber-400"
            data-testid="import-entries-update-toggle"
          />
          Re-import to update
        </label>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.pdf,.rtf,text/csv,application/pdf,application/rtf,text/rtf"
          onChange={handleFile}
          className="hidden"
          data-testid="import-entries-input"
        />
        <button
          type="button"
          onClick={openPicker}
          disabled={busy}
          className="ccs-btn-ghost flex items-center gap-2 text-sm"
          data-testid="import-entries-button"
          title="Upload a CSV, RTF (Word table export), or a results PDF in the standard layout."
        >
          <Upload size={14} /> {busy ? "Working…" : "Import CSV / PDF / RTF"}
        </button>
        <ImportFormatsHelp />
      </div>

      {(stage === "resolving" || stage === "committing" || stage === "done" || error) && (
        <ResolveModal
          stage={stage}
          preview={preview}
          choices={choices}
          setChoices={setChoices}
          result={result}
          error={error}
          updateExisting={updateExisting}
          fileName={pickedFile?.name || ""}
          onCancel={reset}
          onConfirm={commit}
          onClose={reset}
        />
      )}
    </>
  );
}
